import React from 'react';
import { Loader } from '../../v2BaseComponents/Loader';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import { TextFieldOptionsType } from '@aws-amplify/ui-react-native/dist/Authenticator/hooks';
import AmplifyAuthenticationField from './AmplifyAuthenticationField';
import { TextInputProps, View } from 'react-native';
import { getSpacing } from '../../../utils/spacing';
import { useUrlEmail } from '../../../hooks/useUrlAppParams';

type AmplifyAuthenticationFieldsOwnProps = {
    validationErrors?: Record<string, string | string[]>;
    isPending?: boolean;
    fields: TextFieldOptionsType[];
};

type AmplifyAuthenticationFieldsProps = AmplifyAuthenticationFieldsOwnProps;

type Step = 'signIn' | 'signUp' | 'resetPassword' | 'confirmSignUp' | 'confirmResetPassword';

const getErrorMessage = (error: string | string[] | undefined): string | undefined => {
    if (!error) return undefined;
    if (Array.isArray(error)) return error.join(', ');
    return error;
};

const getAdditionalInputProps = (
    fieldType: 'default' | 'password' | 'phone' | 'email',
    step: Step,
    staticValue: string | null,
): {
    autoComplete: TextInputProps['autoComplete'];
    secureTextEntry: boolean;
    onBlur?: () => void;
    staticValue?: string;
} => {
    switch (step) {
        case 'signIn':
            return fieldType === 'password'
                ? { autoComplete: 'current-password', secureTextEntry: true }
                : // There is a bug that can cause the autofill biometric prompt to get
                  // closed when calling the onBlur function. This is a workaround to prevent that.
                  {
                      autoComplete: 'email',
                      secureTextEntry: false,
                      // onBlur: () => {},
                      staticValue: staticValue || undefined,
                  };
        case 'signUp':
            return fieldType === 'password'
                ? { autoComplete: 'new-password', secureTextEntry: true }
                : { autoComplete: 'email', secureTextEntry: false, staticValue: staticValue || undefined };
        case 'resetPassword':
            return { autoComplete: 'email', secureTextEntry: false, staticValue: staticValue || undefined };
        case 'confirmSignUp':
            return { autoComplete: 'one-time-code', secureTextEntry: false };
        case 'confirmResetPassword':
            return fieldType === 'password'
                ? { autoComplete: 'new-password', secureTextEntry: true }
                : { autoComplete: 'one-time-code', secureTextEntry: false };
    }
};

const AmplifyAuthenticationFields: React.FC<AmplifyAuthenticationFieldsProps & { step: Step }> = (props) => {
    const defaultEmail = useUrlEmail();

    return (
        <View
            style={{
                paddingVertical: getSpacing(1),
            }}
        >
            {props.isPending ? (
                <View
                    style={{
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingVertical: getSpacing(16),
                    }}
                >
                    <Loader />
                </View>
            ) : (
                <Stack vertical space={getSpacing(3)}>
                    {props.fields.map((field) => (
                        <AmplifyAuthenticationField
                            key={field.name}
                            errorMessage={getErrorMessage(props.validationErrors?.[field.name])}
                            {...field}
                            {...getAdditionalInputProps(field.type, props.step, defaultEmail)}
                            testID={field.type === 'password' ? 'password' : 'email'}
                        />
                    ))}
                </Stack>
            )}
        </View>
    );
};

const AmplifyAuthenticationSignInFields = (props: AmplifyAuthenticationFieldsProps) => {
    return <AmplifyAuthenticationFields {...props} step="signIn" />;
};

const AmplifyAuthenticationSignUpFields = (props: AmplifyAuthenticationFieldsProps) => {
    return <AmplifyAuthenticationFields {...props} step="signUp" />;
};

const AmplifyAuthenticationConfirmSignUpFields = (props: AmplifyAuthenticationFieldsProps) => {
    return (
        <View style={{ width: '100%', marginTop: getSpacing(contentStackSpacing) }}>
            <AmplifyAuthenticationFields {...props} step="confirmSignUp" />
        </View>
    );
};

const AmplifyAuthenticationResetPasswordFields = (props: AmplifyAuthenticationFieldsProps) => {
    return <AmplifyAuthenticationFields {...props} step="resetPassword" />;
};

const AmplifyAuthenticationConfirmResetPasswordFields = (props: AmplifyAuthenticationFieldsProps) => {
    return <AmplifyAuthenticationFields {...props} step="confirmResetPassword" />;
};

export default {
    SignIn: AmplifyAuthenticationSignInFields,
    SignUp: AmplifyAuthenticationSignUpFields,
    ConfirmSignUp: AmplifyAuthenticationConfirmSignUpFields,
    ResetPassword: AmplifyAuthenticationResetPasswordFields,
    ConfirmResetPassword: AmplifyAuthenticationConfirmResetPasswordFields,
};
